import { InfoIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Checkbox,
  Collapse,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FC, useCallback, useEffect, useState } from 'react';
import { ProviderSearchSelect } from '../../../components/provider-search';
import { CustomFieldValue } from '../../../types';

interface SupervisingPhysicianSelectProps {
  field: CustomFieldValue | undefined;
  onChange: (field: CustomFieldValue) => void;
  isRequired?: boolean;
  error?: string;
}

export const SupervisingPhysicianSelect: FC<
  SupervisingPhysicianSelectProps
> = ({ field, onChange, isRequired = false, error }) => {
  const toast = useToast();

  // Get current values
  const currentValue = field?.value || '';
  const currentProviders = (() => {
    if (field?.metadata?.providers) {
      return (
        field.metadata.providers as Array<{
          providerId: string;
          providerLabel: string;
        }>
      ).filter((provider) => provider.providerId);
    }
    return [];
  })();

  // Local state for checkbox and free text
  const [freeTextWithProviderSearch, setFreeTextWithProviderSearch] =
    useState<boolean>(currentProviders.length > 0 || !!currentValue);
  const [freeTextName, setFreeTextName] =
    useState<string>(currentValue);

  // Validation state
  const [validationError, setValidationError] = useState<string>('');

  // Update field value and metadata
  const updateField = useCallback(
    (
      value: string,
      providers: { providerId: string; providerLabel: string }[] = []
    ) => {
      const updatedField: CustomFieldValue = {
        // Preserve existing IDs
        id: field?.id,
        fieldId: field?.fieldId || '',
        rowId: field?.rowId || null,
        startDate: field?.startDate || null,
        endDate: field?.endDate || null,
        // Update value and metadata
        value: value,
        metadata: {
          providers: providers,
          freeTextName: value, // Store free text in metadata for backward compatibility
        },
      };

      onChange(updatedField);
    },
    [field, onChange]
  );

  // Validate the current input
  const validateInput = useCallback(() => {
    if (
      isRequired &&
      !freeTextName.trim() &&
      currentProviders.length === 0
    ) {
      return 'Either free text or linked providers are required';
    }

    if (freeTextName.trim().length > 500) {
      return 'Free text cannot exceed 500 characters';
    }

    return '';
  }, [freeTextName, currentProviders, isRequired]);

  // Handle free text name changes with validation
  const handleFreeTextNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value;
      setFreeTextName(name);

      // Clear validation error when user starts typing
      if (validationError) {
        setValidationError('');
      }

      updateField(name, currentProviders);
    },
    [currentProviders, updateField, validationError]
  );

  // Handle provider changes
  const handleProvidersChange = useCallback(
    (
      providers: {
        id: string;
        label: string;
        specialty?: string;
        practice?: string;
      }[]
    ) => {
      const convertedProviders = providers.map((provider) => ({
        providerId: provider.id,
        providerLabel: provider.label,
      }));

      // Clear validation error when providers are added
      if (validationError) {
        setValidationError('');
      }

      updateField(freeTextName, convertedProviders);
    },
    [freeTextName, updateField, validationError]
  );

  // Handle checkbox toggle
  const handleCheckboxToggle = useCallback(
    (isChecked: boolean) => {
      setFreeTextWithProviderSearch(isChecked);
      if (!isChecked) {
        // Clear providers when unchecking
        updateField(freeTextName, []);
        toast({
          title: 'Providers cleared',
          description:
            'Linked providers have been removed. You can still use free text.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [freeTextName, updateField, toast]
  );

  // Validate on blur
  const handleBlur = useCallback(() => {
    const error = validateInput();
    setValidationError(error);
  }, [validateInput]);

  // Hydrate on edit when metadata already exists
  useEffect(() => {
    if (!field) return;
    const hasProviders =
      Array.isArray(field.metadata?.providers) &&
      (
        field.metadata?.providers as Array<{
          providerId: string;
          providerLabel: string;
        }>
      ).length > 0;
    const hasFree = !!(field.value && String(field.value).trim());
    setFreeTextWithProviderSearch(hasProviders || hasFree);
  }, [field]);

  // Get the final error to display
  const finalError = error || validationError;

  return (
    <Box width="100%">
      <VStack align="stretch" spacing={4} width="100%">
        {/* Free Text Input */}
        <Box width="100%">
          <FormControl
            isInvalid={!!finalError}
            isRequired={isRequired}
            width="100%"
          >
            <FormLabel
              fontSize="sm"
              fontWeight="medium"
              color="gray.700"
            >
              Supervising Physician Information
            </FormLabel>
            <Input
              value={freeTextName}
              onChange={handleFreeTextNameChange}
              onBlur={handleBlur}
              placeholder="Enter supervising physician name or details"
              size="md"
              borderRadius="md"
              borderColor={finalError ? 'red.300' : 'gray.300'}
              _hover={{
                borderColor: finalError ? 'red.400' : 'gray.400',
              }}
              _focus={{
                borderColor: finalError ? 'red.500' : 'blue.500',
                boxShadow: finalError
                  ? '0 0 0 1px var(--chakra-colors-red-500)'
                  : '0 0 0 1px var(--chakra-colors-blue-500)',
              }}
              width="100%"
            />
            <FormErrorMessage>{finalError}</FormErrorMessage>
            <HStack mt={1} spacing={2} width="100%">
              <Text fontSize="0.75rem" color="gray.500">
                Free text description
              </Text>
              {freeTextName && (
                <Badge size="sm" colorScheme="blue" variant="subtle">
                  {freeTextName.length}/500
                </Badge>
              )}
            </HStack>
          </FormControl>
        </Box>

        {/* Optional Provider Search Checkbox */}
        <Box
          width="100%"
          p="1rem"
          bg="blue.50"
          borderRadius="md"
          border="1px solid"
          borderColor="blue.200"
          _hover={{
            borderColor: 'blue.300',
            bg: 'blue.100',
          }}
          transition="all 0.2s"
        >
          <HStack spacing={3} align="flex-start" width="100%">
            <Checkbox
              isChecked={freeTextWithProviderSearch}
              onChange={(e) => handleCheckboxToggle(e.target.checked)}
              colorScheme="blue"
              size="md"
            />
            <Box flex={1} width="100%">
              <Text fontSize="sm" fontWeight="500" color="blue.700">
                Also search and select specific providers to link?
              </Text>
              <Text fontSize="0.75rem" color="blue.600" mt={1}>
                This will allow you to link to actual provider
                profiles in addition to the free text
              </Text>
            </Box>
            <Tooltip label="Linked providers will be clickable and navigate to their detail pages">
              <IconButton
                aria-label="Information about provider linking"
                icon={<InfoIcon />}
                size="sm"
                variant="ghost"
                colorScheme="blue"
              />
            </Tooltip>
          </HStack>
        </Box>

        {/* Provider Search Section - Show only when checkbox is checked */}
        <Collapse in={freeTextWithProviderSearch} animateOpacity>
          <Box
            width="100%"
            p="1.5rem"
            bg="gray.50"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            _hover={{
              borderColor: 'gray.300',
              boxShadow: 'sm',
            }}
            transition="all 0.2s"
          >
            <HStack
              justify="space-between"
              align="center"
              mb="1rem"
              width="100%"
            >
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                Search and Select Providers
              </Text>
              <Badge colorScheme="blue" variant="subtle">
                {currentProviders.length} linked
              </Badge>
            </HStack>

            <Box width="100%" sx={{ '& > *': { width: '100%' } }}>
              <ProviderSearchSelect
                selectedProviders={currentProviders.map((p) => ({
                  id: p.providerId,
                  label: p.providerLabel,
                  specialty: '',
                  practice: '',
                }))}
                onProvidersChange={handleProvidersChange}
                placeholder="Search for supervising physicians"
              />
            </Box>
          </Box>
        </Collapse>

        {/* Summary Display */}
        {(freeTextName || currentProviders.length > 0) && (
          <Box
            width="100%"
            p="1rem"
            bg="green.50"
            borderRadius="md"
            border="1px solid"
            borderColor="green.200"
            _hover={{
              borderColor: 'green.300',
              boxShadow: 'sm',
              transform: 'translateY(-1px)',
            }}
            transition="all 0.2s"
          >
            <HStack justify="space-between" align="flex-start">
              <Box flex={1}>
                <Text
                  fontSize="sm"
                  fontWeight="500"
                  color="green.700"
                >
                  Summary:
                </Text>
                {freeTextName && (
                  <Text fontSize="sm" color="green.600" mt={1}>
                    <strong>Description:</strong> {freeTextName}
                  </Text>
                )}
                {currentProviders.length > 0 && (
                  <Text fontSize="sm" color="green.600" mt={1}>
                    <strong>Linked Providers:</strong>{' '}
                    {currentProviders.length} provider
                    {currentProviders.length !== 1 ? 's' : ''}{' '}
                    selected
                  </Text>
                )}
              </Box>
            </HStack>
          </Box>
        )}

        {/* Help Text */}
        <Box
          width="100%"
          p="0.75rem"
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          <Text fontSize="0.75rem" color="gray.600">
            <strong>Tip:</strong> You can use either free text
            description, linked provider profiles, or both. Linked
            providers will be clickable in the provider detail view
            and navigate to their profiles.
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};
